// ignore-string-localization

import styled, { css } from 'styled-components';
import { hexToRgba } from '../../../../features/src/color-conversion';
import {
  cssColorValue,
  PopoverNavigationItem,
  Type,
  screenXsMax,
  screenSmMax,
  screenMdMin,
  spacer4,
  spacer8,
  spacer16,
  spacer20,
  spacer32,
  spacer40,
  spacer48,
} from '@spotify-internal/encore-web';
import {
  ButtonPrimaryAsLinkV6 as ButtonPrimaryAsLink,
  ButtonSecondaryAsLinkV6 as ButtonSecondaryAsLink,
  getLinkStyle,
} from '../../ui';
import { TWO_COLUMN_MAX_WIDTH } from '@spotify-internal/frodor-modules';

type NavBarProps = {
  customBackground?: string;
  customTextColor?: string;
  bleed?: boolean;
  isScrolling?: boolean;
};

export const StyledListItemWithMargin = styled.li`
  margin-inline-start: ${spacer8};
  & > a:focus > div:last-child {
    border-color: ${props =>
      props.color || cssColorValue('backgroundBase')} !important;
  }
`;

export const StyledListItemNoMargin = styled.li`
  & > a:focus > div:last-child {
    border-color: ${props =>
      props.color || cssColorValue('backgroundBase')} !important;
  }
`;

export const LogoArea = styled.div<any>`
  /* Logo and actions area are same width, so links are centered on the screen */
  width: fit-content;

  @media (max-width: ${screenSmMax}) {
    height: 28px;
  }

  @media (min-width: ${screenMdMin}) {
    height: 36px;
  }
`;

export const MenuLink = styled(Type).attrs({
  weight: 'bold',
})``;

export const MenuCategory = styled.div`
  width: max-content;
  svg {
    margin-left: ${spacer8};
  }
`;

export const ClaimButton = styled(ButtonPrimaryAsLink).attrs({
  buttonSize: 'sm',
})<NavBarProps>`
  white-space: nowrap;
`;

export const LoginButton = styled(ButtonSecondaryAsLink).attrs({
  buttonSize: 'sm',
})`
  white-space: nowrap;
`;

export const NavBarWrapper = styled.header<NavBarProps>`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;

  background: ${props =>
    props.isScrolling
      ? props.customBackground || props.theme.nav.background
      : 'transparent'};
  color: ${props => props.customTextColor || props.theme.nav.text};

  display: flex;
  align-items: center;
  justify-content: flex-start;

  /* top padding to help prevent flicker when scrolling down */
  ${({ bleed }) =>
    !bleed &&
    css`
      background: ${props => props.theme.nav.background};
      + main {
        padding-top: 66px;
      }
      @media (max-width: ${screenXsMax}) {
        + main {
          padding-top: 48px;
        }
      }
    `}

  ul,
  ol {
    list-style: none;
    padding-left: 0;
    li {
      height: 100%;
      div:first-child {
        height: 100%;
      }
    }
  }

  li {
    line-height: 20px;
  }

  /* default link colors */
  ${MenuLink}, ${MenuCategory}, ${ClaimButton}, ${LoginButton} {
    &,
    a,
    a:visited,
    button {
      color: ${props => props.customTextColor || props.theme.nav.text};
      vertical-align: top;
      ${(props: any) => getLinkStyle(props)}
    }
  }

  ${MenuCategory} button {
    /* Menu titles are not links, but should look like them */
    color: ${props =>
      hexToRgba(props.customTextColor || props.theme.nav.text, 0.75)};
    &:hover {
      color: ${props => props.customTextColor || props.theme.nav.text};
    }
  }

  /* logo drawn in text color (not shaded) */
  ${LogoArea} a {
    svg {
      fill: ${props => props.customTextColor || props.theme.nav.text};
    }
  }

  /* sign in/out button coloring */
  ${LoginButton}:not(:disabled) {
    background: transparent;
    border: 1px solid ${props => props.customTextColor || props.theme.nav.text};
    color: ${props => props.customTextColor || props.theme.nav.text};

    &:hover:not(:disabled) {
      color: ${props => props.customTextColor || props.theme.nav.text};
      background: transparent;
    }
  }

  /* claim/dashboard button coloring */
  ${ClaimButton}:not(:disabled) {
    background: ${props => props.customTextColor || props.theme.nav.text};
    border: 1px solid ${props => props.customTextColor || props.theme.nav.text};
    color: ${props => props.customBackground || props.theme.nav.background};
    &:hover:not(:disabled),
    :focus {
      color: ${props => props.customBackground || props.theme.nav.background};
      background: ${props => props.customTextColor || props.theme.nav.text};
    }
  }

  @media (min-width: 1440px) {
    padding: ${spacer20} ${spacer48};
  }

  @media (max-width: 1439px) {
    padding: ${spacer20} ${spacer40};
  }

  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    padding: 4px ${spacer40};
  }

  @media (max-width: ${screenXsMax}) {
    padding: 4px ${spacer16};
  }

  ul ul {
    flex-flow: column nowrap;
  }

  ${PopoverNavigationItem} {
    a {
      &,
      &:focus,
      &:focus:not(:disabled),
      &:hover,
      &:active,
      &:active:not(:disabled),
      &:visited {
        color: ${cssColorValue('textBase')} !important;
        padding-top: ${spacer4};
        padding-bottom: 0;
        &:last-child {
          padding-bottom: ${spacer4};
        }
      }
    }
  }
`;

export const LinksArea = styled.nav`
  direction: ltr;
  flex: 1;

  ul {
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: baseline;
    padding: 0;
    margin: auto;
    li {
      margin-right: ${spacer32};
      &: last-child {
        margin-right: 0px;
      }
    }
  }

  /* using an id selector to avoid applying rule to Resources dropdown */
  #menu-links {
    /* Encore v4 button designs are smaller than the previous iteration. Slight right padding keeps these links centered */
    padding: 0 0 0 0;
  }

  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    display: none;
  }
`;

export const ActionsArea = styled.div`
  width: max-content;
  /* fixing the height here to the button height so that the nav doesn't flicker,
     due to initially rendering in "loading" state (no user, no buttons) */
  height: ${spacer32};

  ul {
    display: flex;
    flex-flow: row nowrap;
    padding: 0;
  }

  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    display: none;
  }
`;

export const MenuButtonArea = styled.div<{
  customTextColor?: string;
}>`
  flex: 1;
  text-align: right;
  svg {
    fill: ${props => props.customTextColor || props.theme.nav.text} !important;
  }

  @media (min-width: ${TWO_COLUMN_MAX_WIDTH}) {
    display: none;
  }
`;

export const StyledPopoverNavigationItem = styled(PopoverNavigationItem)<{
  features?: boolean;
}>`
  margin-right: 0px !important;
  ${({ features }) => (features ? 'padding-left: 12px' : '')};
  width: 100%;
  & > a {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    svg {
      display: none;
    }
    :hover,
    :focus {
      background-color: ${cssColorValue(
        'backgroundElevatedHighlight',
      )} !important;
      svg {
        display: inline;
        vertical-align: -3px;
      }
    }
    :focus:not(:focus-visible) {
      box-shadow: none !important;
    }
  }
`;
